import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV, faFileAlt, faComment, faMusic, faTachometerAlt } from '@fortawesome/free-solid-svg-icons';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { getRoleLabel } from '../../../../../constants/songRoles';

/**
 * Individual song item component in the setlist
 * 
 * @param {object} song - Song data
 * @param {boolean} isSelected - Whether the song is selected in edit mode
 * @param {boolean} editMode - Whether the setlist is in edit mode
 * @param {boolean} hasPart - Whether the song has a part file
 * @param {boolean} hasComment - Whether the song has a comment
 * @param {function} onSelect - Function to handle selection in edit mode
 * @param {function} onClick - Function to handle click on the song
 * @param {function} onOptionsClick - Function to handle options button click
 * @param {function} onCommentClick - Function to handle comment icon click
 * @returns {JSX.Element} Setlist item component
 */
const SetlistItem = ({
  song,
  isSelected,
  editMode,
  hasPart = false,
  hasComment = false,
  onSelect,
  onClick,
  onOptionsClick,
  onCommentClick
}) => {
  const { t } = useLanguage();

  // State for comment tooltip visibility
  const [showCommentPreview, setShowCommentPreview] = useState(false);
  
  // Handle comment click
  const handleCommentClick = (e) => {
    e.stopPropagation();
    if (onCommentClick) {
      onCommentClick(song.song_id);
    }
  };

  // Create tooltip for comment
  const commentTooltip = (
    <Tooltip id={`comment-tooltip-${song.song_id}`}>
      {song.comment && song.comment.length > 100 
        ? `${song.comment.substring(0, 100)}...` 
        : song.comment}
    </Tooltip>
  );

  // More vibrant light blue color for part files - easier to see on dark background
  const partFileColor = '#4dabf7'; // Lighter blue shade

  return (
    <div 
      className={`event-setlist-item ${isSelected ? 'selected' : ''} ${editMode ? 'edit-mode' : ''} ${hasPart ? 'has-part' : ''}`}
      onClick={() => {
        if (editMode) {
          onSelect(song.song_id);
        } else {
          onClick(song.song_id);
        }
      }}
    >
      {/* Checkbox for edit mode */}
      {editMode && (
        <div className="song-select-checkbox">
          <input 
            type="checkbox" 
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect(song.song_id);
            }}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
      
      <div className="event-setlist-item-left">
        <div className="event-setlist-details">
          <div className="event-setlist-title" style={{ 
            color: hasPart ? partFileColor : '#ffffff', 
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}>
            {/* Comment icon for songs with comments */}
            {hasComment && (
              <OverlayTrigger
                placement="top"
                overlay={commentTooltip}
                trigger={['hover', 'focus']}
              >
                <span onClick={handleCommentClick}>
                  <FontAwesomeIcon 
                    icon={faComment} 
                    className="setlist-comment-icon me-1" 
                  />
                </span>
              </OverlayTrigger>
            )}
            
            {song.title}
          </div>
          
          <div className="event-setlist-details-row">
            {/* Song role */}
            <div className="event-setlist-role">{getRoleLabel(song.song_role, t)}</div>
            
            {/* Song key and tempo info */}
            <div className="event-setlist-metadata">
              {song.key && (
                <div className="event-setlist-key">
                  <FontAwesomeIcon 
                    icon={faMusic} 
                    className="me-1" 
                    style={{ fontSize: '0.7rem', opacity: 0.7 }} 
                  />
                  <span>{song.key}</span>
                </div>
              )}
              
              {song.tempo && (
                <div className="event-setlist-tempo">
                  <FontAwesomeIcon 
                    icon={faTachometerAlt} 
                    className="me-1" 
                    style={{ fontSize: '0.7rem', opacity: 0.7 }} 
                  />
                  <span>{song.tempo}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="event-setlist-item-right">
        {/* Only show options button in normal mode, not in edit mode */}
        {!editMode && (
          <div className="event-song-options">
            <FontAwesomeIcon 
              icon={faEllipsisV} 
              className="event-setlist-icon" 
              onClick={(e) => {
                e.stopPropagation(); // Stop event from bubbling to parent
                onOptionsClick(e, song.song_id);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SetlistItem;